import { request } from './client'

/* ============================================================================
   CQ-136 — WHAT EACH KIND OF ROOM NEEDS FOR EACH KIND OF CLEAN.

   The numbers have been OURS since Sprint 40 — two bath towels, two hand towels, one bath
   mat, taken from the approved pack's own M-03 screen — and every place they appear says
   so. The client's answer on 17 September was *"start from ours, they correct it"*, which
   is only an answer if there is somewhere to do the correcting.

   **A NUMBER A CUSTOMER CANNOT CHANGE IS A NUMBER THAT STAYS WRONG**, and this product has
   that recorded as defect #90.
   ========================================================================= */

/** A room type or a kind of work. `code` is never translated — it is what a report
 *  counts by, and what the next spreadsheet upload matches on. */
export interface LinenStandardKind {
  id: string
  code: string
  name: string
  nameAr: string | null
  /** How many people a room of this type sleeps. Null for a kind of work.
   *  It is here because it is the number somebody is looking at when they decide
   *  how many towels a room needs. */
  capacity: number | null
}

export interface LinenStandardItem {
  stockItemId: string
  code: string
  name: string
  nameAr: string | null
  unit: string
  sortOrder: number
}

export interface LinenStandardLine {
  roomTypeId: string
  taskTypeId: string
  stockItemId: string
  quantity: number
  sortOrder: number
}

export interface LinenStandardBoard {
  roomTypes: LinenStandardKind[]
  /** Every kind of work, **including the ones with no linen against them**. Touch-up has
   *  none and should not — but it has to be visible and empty rather than absent, or the
   *  screen silently answers a different question from the one it was asked. */
  taskTypes: LinenStandardKind[]
  items: LinenStandardItem[]
  lines: LinenStandardLine[]
}

export const fetchLinenStandards = (propertyId: string) =>
  request<LinenStandardBoard>(`/properties/${propertyId}/linen-standards`)

/**
 * One cell.
 *
 * **QUANTITY ZERO REMOVES THE STANDARD**, it does not store a zero: a standard of zero is
 * the row's absence said twice, and a screen an attendant reads in a corridor earns
 * nothing by listing what a room does not need.
 *
 * Returns the whole board, so the screen redraws from what the server actually holds
 * rather than from what it hoped it had written.
 */
export const saveLinenStandard = (
  propertyId: string,
  line: { roomTypeId: string; taskTypeId: string; stockItemId: string; quantity: number },
) => request<LinenStandardBoard>(`/properties/${propertyId}/linen-standards`, {
  method: 'PUT',
  body: line,
})
